/**
 * Cihazın COM port profilleri (tartı vb.) — sistem rolü. Alan adları System.IO.Ports.SerialPort
 * ile bire bir (PortName, BaudRate, Parity…): teknik kimlikler olduğu için etiketleri
 * çevrilmez, ajanla ve dokümanla aynı adı taşır. "Test oku" ajana gidip canlı değeri getirir.
 */
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Scale, Trash2 } from 'lucide-react';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Dialog } from '@/shared/ui/Dialog';
import { IconButton } from '@/shared/ui/IconButton';
import { Input } from '@/shared/ui/Input';
import { Label } from '@/shared/ui/Label';
import { dialogs } from '@/shared/ui/dialogStore';
import { queryKeys } from '@/shared/api/queryKeys';
import { comPortApi, type ComPortReadingDto } from '@/shared/api/comPortApi';
import { cn } from '@/shared/lib/cn';
import { deviceApi, type DeviceComPortDto, type DeviceDto } from '../api/deviceApi';

type ComPortForm = Omit<DeviceComPortDto, 'deviceComPortId'>;

const PARITY = ['None', 'Odd', 'Even', 'Mark', 'Space'];
const STOP_BITS = ['One', 'Two', 'OnePointFive'];
const HANDSHAKE = ['None', 'XOnXOff', 'RequestToSend', 'RequestToSendXOnXOff'];
const ENCODING = ['ASCII', 'UTF8', 'Latin1'];
const READ_MODE = ['Continuous', 'Command'];
const BAUD = [1200, 2400, 4800, 9600, 19200, 38400, 57600, 115200];

const emptyForm = (portName: string): ComPortForm => ({
  comPortAlias: '',
  portName,
  baudRate: 9600,
  dataBits: 8,
  parity: 'None',
  stopBits: 'One',
  handshake: 'None',
  dtrEnable: false,
  rtsEnable: false,
  readTimeout: 500,
  writeTimeout: 500,
  newLine: '\\r\\n',
  encoding: 'ASCII',
  readMode: 'Continuous',
  command: null,
  parsePattern: null,
  stableReadCount: 3,
  stableWindowMs: 1500,
  active: true,
});

export function ComPortsEditor({ device }: { device: DeviceDto }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ id: string | null; form: ComPortForm } | null>(null);
  const [tests, setTests] = useState<Record<string, { loading: boolean; reading: ComPortReadingDto | null }>>({});
  const ports = device.comPorts ?? [];
  const invalidate = () => qc.invalidateQueries({ queryKey: queryKeys.device.all });

  const remove = useMutation({
    mutationFn: (id: string) => deviceApi.deleteComPort(id),
    onSuccess: () => void invalidate(),
  });

  const test = async (p: DeviceComPortDto) => {
    setTests((prev) => ({ ...prev, [p.deviceComPortId]: { loading: true, reading: null } }));
    let reading: ComPortReadingDto | null = null;
    try {
      reading = await comPortApi.read(p.comPortAlias, 4000);
    } catch (err) {
      reading = { comPortAlias: p.comPortAlias, value: null, raw: null, stable: false, readAtUtc: '', error: err instanceof Error ? err.message : String(err) };
    }
    setTests((prev) => ({ ...prev, [p.deviceComPortId]: { loading: false, reading } }));
  };

  return (
    <div className="mt-1 pl-4">
      <div className="text-fg-muted flex items-center gap-2 text-xs">
        <Scale className="h-3.5 w-3.5" />
        <span>{t('shell.user.devicesComPorts')}</span>
        {device.serialPortNames?.length ? <span className="text-fg-subtle">({device.serialPortNames.join(', ')})</span> : null}
        <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setEditing({ id: null, form: emptyForm(device.serialPortNames?.[0] ?? 'COM1') })}>
          <Plus className="h-3.5 w-3.5" /> {t('shell.user.devicesComPortAdd')}
        </Button>
      </div>
      {ports.length > 0 ? (
        <ul className="mt-1 flex flex-col gap-1">
          {ports.map((p) => {
            const tr = tests[p.deviceComPortId];
            return (
              <li key={p.deviceComPortId} className={cn('bg-bg-muted flex flex-wrap items-center gap-2 rounded px-2 py-1 text-xs', !p.active && 'opacity-60')}>
                <span className="font-mono font-semibold">{p.comPortAlias}</span>
                <span className="text-fg-muted">
                  {p.portName} · {p.baudRate},{p.dataBits},{p.parity[0]},{p.stopBits} · {p.readMode}
                </span>
                {tr?.loading ? (
                  <span className="text-fg-subtle">{t('actions.sp.comReading')}</span>
                ) : tr?.reading ? (
                  tr.reading.error ? (
                    <span className="text-danger">{tr.reading.error}</span>
                  ) : (
                    <span className={tr.reading.stable ? 'text-success' : 'text-warning'}>
                      {tr.reading.value} {tr.reading.stable ? '' : `(${t('actions.sp.comUnstable')})`}
                      {tr.reading.raw ? <span className="text-fg-subtle"> · {tr.reading.raw.trim()}</span> : null}
                    </span>
                  )
                ) : null}
                <span className="ml-auto flex items-center gap-0.5">
                  <Button variant="ghost" size="sm" onClick={() => void test(p)} disabled={!device.online || !!tr?.loading} title={device.online ? undefined : t('shell.user.devicesOffline')}>
                    {t('shell.user.devicesComPortTest')}
                  </Button>
                  <IconButton label={t('common:Edit')} size="sm" onClick={() => setEditing({ id: p.deviceComPortId, form: { ...p } })}>
                    <Pencil className="h-3.5 w-3.5" />
                  </IconButton>
                  <IconButton
                    label={t('common:Delete')}
                    size="sm"
                    onClick={() => {
                      void dialogs
                        .confirm({ message: t('shell.user.devicesComPortDeleteConfirm', { alias: p.comPortAlias }), kind: 'standard' })
                        .then((ok) => ok && remove.mutate(p.deviceComPortId));
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </IconButton>
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
      {editing ? (
        <ComPortFormDialog
          device={device}
          id={editing.id}
          initial={editing.form}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void invalidate();
          }}
        />
      ) : null}
    </div>
  );
}

function ComPortFormDialog({ device, id, initial, onClose, onSaved }: { device: DeviceDto; id: string | null; initial: ComPortForm; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ComPortForm>(initial);
  const set = <K extends keyof ComPortForm>(key: K, value: ComPortForm[K]) => setForm((prev) => ({ ...prev, [key]: value }));
  const save = useMutation({
    mutationFn: () => (id ? deviceApi.updateComPort(id, form) : deviceApi.createComPort(device.deviceId, form)),
    onSuccess: onSaved,
  });
  const error = save.error ? ((save.error as { message?: string })?.message ?? String(save.error)) : null;
  const num = (key: keyof ComPortForm) => (e: React.ChangeEvent<HTMLInputElement>) => set(key, Number(e.target.value) as never);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`${device.deviceCode} · ${id ? initial.comPortAlias : t('shell.user.devicesComPortAdd')}`} size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common:Cancel')}</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!form.comPortAlias.trim() || !form.portName.trim()}>{t('common:Save')}</Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="ComPortAlias" hint="SP alanında {com:ALIAS}">
          <Input value={form.comPortAlias} onChange={(e) => set('comPortAlias', e.target.value.toUpperCase())} placeholder="TARTI1" />
        </Field>
        <Field label="PortName">
          {device.serialPortNames?.length ? (
            <Select value={form.portName} onChange={(v) => set('portName', v)} options={[...new Set([...device.serialPortNames, form.portName])].map((p) => [p, p])} />
          ) : (
            <Input value={form.portName} onChange={(e) => set('portName', e.target.value.toUpperCase())} placeholder="COM3" />
          )}
        </Field>
        <Field label="BaudRate">
          <Select value={String(form.baudRate)} onChange={(v) => set('baudRate', Number(v))} options={[...new Set([...BAUD, form.baudRate])].sort((a, b) => a - b).map((b) => [String(b), String(b)])} />
        </Field>
        <Field label="DataBits">
          <Select value={String(form.dataBits)} onChange={(v) => set('dataBits', Number(v))} options={[5, 6, 7, 8].map((d) => [String(d), String(d)])} />
        </Field>
        <Field label="Parity"><Select value={form.parity} onChange={(v) => set('parity', v)} options={PARITY.map((p) => [p, p])} /></Field>
        <Field label="StopBits"><Select value={form.stopBits} onChange={(v) => set('stopBits', v)} options={STOP_BITS.map((p) => [p, p])} /></Field>
        <Field label="Handshake"><Select value={form.handshake} onChange={(v) => set('handshake', v)} options={HANDSHAKE.map((p) => [p, p])} /></Field>
        <Field label="Encoding"><Select value={form.encoding} onChange={(v) => set('encoding', v)} options={ENCODING.map((p) => [p, p])} /></Field>
        <Field label="ReadTimeout (ms)"><Input type="number" min={0} value={form.readTimeout} onChange={num('readTimeout')} /></Field>
        <Field label="WriteTimeout (ms)"><Input type="number" min={0} value={form.writeTimeout} onChange={num('writeTimeout')} /></Field>
        <Field label="NewLine" hint="\r\n"><Input value={form.newLine} onChange={(e) => set('newLine', e.target.value)} /></Field>
        <div className="flex items-end gap-4 pb-2">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.dtrEnable} onCheckedChange={(v) => set('dtrEnable', v === true)} /> DtrEnable</label>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.rtsEnable} onCheckedChange={(v) => set('rtsEnable', v === true)} /> RtsEnable</label>
        </div>
        <Field label="ReadMode" hint="Continuous: cihaz sürekli yayınlar · Command: komut gönderilir">
          <Select value={form.readMode} onChange={(v) => set('readMode', v)} options={READ_MODE.map((p) => [p, p])} />
        </Field>
        <Field label="Command" hint="Command kipinde porta yazılır, kaçışlı (W\r\n)">
          <Input value={form.command ?? ''} onChange={(e) => set('command', e.target.value || null)} disabled={form.readMode !== 'Command'} />
        </Field>
        <Field label="ParsePattern" hint="Regex; ilk yakalama grubu sayı. Boşsa ilk ondalık sayı.">
          <Input value={form.parsePattern ?? ''} onChange={(e) => set('parsePattern', e.target.value || null)} placeholder="([-+]?\d+[.,]?\d*)\s*kg" />
        </Field>
        <Field label="StableReadCount"><Input type="number" min={1} value={form.stableReadCount} onChange={num('stableReadCount')} /></Field>
        <Field label="StableWindowMs"><Input type="number" min={100} step={100} value={form.stableWindowMs} onChange={num('stableWindowMs')} /></Field>
        <div className="flex items-end pb-2">
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={form.active} onCheckedChange={(v) => set('active', v === true)} /> Active</label>
        </div>
      </div>
      {error ? <Alert tone="danger" className="mt-3">{error}</Alert> : null}
    </Dialog>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      {children}
      {hint ? <span className="text-fg-subtle text-[11px]">{hint}</span> : null}
    </div>
  );
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="border-border bg-bg-elevated text-fg focus-visible:border-brand-500 focus-visible:ring-ring/40 h-[var(--control-h)] w-full rounded-md border px-2 text-sm focus-visible:ring-2 focus-visible:outline-none"
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}
